import { useCallback, useEffect, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import {
  VoicemailBox,
  VoicemailMessage,
} from '@weave/schema-gen-ts/dist/schemas/phone-exp/phone-records/v1/voicemail.pb';
import type { Tag } from '@weave/schema-gen-ts/dist/schemas/tag/shared/v1/models.pb';
import { VoicemailEventType } from '@weave/schema-gen-ts/dist/shared/phone/v1/voicemail/message.pb';
import dayjs from 'dayjs';
import { useQueryClient, InfiniteData } from 'react-query';
import { PhoneCallsQueries } from '@frontend/api-phone-calls';
import { TagsV2 } from '@frontend/api-tag';
import { VoicemailApi, VoicemailTypes } from '@frontend/api-voicemails';
import { EmptyStateConfig } from '@frontend/components';
import { useTranslation, i18next } from '@frontend/i18n';
import { usePhoneConfigShallowStore } from '@frontend/phone-config';
import { isPartialPhoneNumber, isPhoneNumber, sanitizePhoneNumber } from '@frontend/phone-numbers';
import { useQueueStore } from '@frontend/queue';
import { breakpoints, useTargetElementContainerQuery } from '@frontend/responsiveness';
import { useAppScopeStore, useOnScopeChange, useScopedInfiniteQuery } from '@frontend/scope';
import {
  AllCallsTable,
  PaginationSettings,
  VoicemailFiltersType,
  usePhonePageSettingShallowStore,
} from '../../hooks/use-phone-config-state-store';
import { useVoicemailBoxList } from '../../hooks/use-voicemail-box';
import { useVoicemailMutations } from '../../hooks/use-voicemail-mutations';
import { queryKeys } from '../../query-keys';
import { useVoicemailDetailsShallowStore } from '../../stores/voicemail-details-store';
import { defaultDateRangeMap, formatEndDateWithTimeZone, formatStartDateWithTimeZone } from '../../utils';
import { useVoicemailEventSubscription } from '../../voicemail-event-emitter';
import {
  MessageStatus,
  VoicemailInfiniteQueryData,
  VoicemailTableDataRow,
  VoicemailQueryData,
} from '../all-calls/types';
import { getTimePeriod } from '../all-calls/utils';
import { DEFAULT_NO_TIME_PERIOD } from '../filter-selectors/time-period-selector';
import { useVoicemailActionsGetter } from './voicemail-actions';
import { SmallView } from './voicemail-mobile-view-components';
import { VoicemailTableView } from './voicemail-table-view-components';
import { updateVoicemailData, useAddVoicemailMessage, useVoicemailTagEvent } from './voicemail-update';
import { createVoicemailObject } from './voicemail-utils';
import { Forwardvoicemail, useForwardVoicemailModalControl } from './voicemails-forward';

type MultiVoicemailProps = {
  setSelectedTimerange: (subtitle: string) => void;
};

export type ForwardVoicemailDynamicData = {
  rowIndex: number;
  vmId: string;
  currentVoicemailBoxName: string;
  currentVoicemailBoxId: string;
  mediaPath: string;
  mediaId: string;
};

type ForwardVoicemailStaticData = {
  voicemailBoxList: VoicemailBox[];
  onDelete: (req: VoicemailTypes.DeletePayload) => void;
};

export type ForwardVoicemailModalData = ForwardVoicemailDynamicData & ForwardVoicemailStaticData;

export const defaultForwardVoicemailModalData = {};

export type SetPageConfigInput = Partial<PaginationSettings> | ((_: PaginationSettings) => Partial<PaginationSettings>);

const defaultEmptyState = {
  type: 'voicemail',
  header: i18next.t('No Items to Display'),
  description: i18next.t('Voicemails will display here.'),
};

export const getMailboxToNameMap = (voicemailBoxes: VoicemailBox[]) => {
  return voicemailBoxes.reduce((acc, { mailbox }) => {
    acc[mailbox?.id ?? '-'] = mailbox?.name ?? '-';
    return acc;
  }, {} as Record<string, string>);
};

const getDefaultFilters: (sipId: string) => VoicemailFiltersType = (sipId) => ({
  locationIds: [],
  syncDeviceSipProfileId: sipId,
  filterOptions: {
    callerNumber: '',
    callerName: '',
    endTime: defaultDateRangeMap[DEFAULT_NO_TIME_PERIOD].endDate,
    startTime: defaultDateRangeMap[DEFAULT_NO_TIME_PERIOD].startDate,
    status: MessageStatus.MESSAGE_STATUS_ALL,
    mailboxIds: [],
    tagIds: [],
  },
});

export const VoicemailPage = ({ setSelectedTimerange }: MultiVoicemailProps) => {
  const { t } = useTranslation('calls', { keyPrefix: 'voicemails' });
  const queryClient = useQueryClient();

  const { selectedOrgId, selectedLocationIds } = useAppScopeStore();
  // const showEnabledFiltersBadgeHandler = useRef<((showNotification: boolean) => void) | undefined>(undefined);

  const { phoneConfig } = usePhoneConfigShallowStore('phoneConfig');
  const {
    config,
    setPageSize,
    setPageNumber,
    setFilters: setVoicemailPageFilters,
  } = usePhonePageSettingShallowStore('config', 'setPageSize', 'setPageNumber', 'setFilters');

  const [emptyState, setEmptyState] = useState<EmptyStateConfig>(defaultEmptyState as EmptyStateConfig);

  const defaultFilters = getDefaultFilters(phoneConfig?.sipProfileId ?? '');
  const pageNumber = config[AllCallsTable.Voicemails].pageNumber;
  const pageSize = config[AllCallsTable.Voicemails].pageSize;

  const setPageConfig = useCallback(
    (input: SetPageConfigInput) => {
      const data = typeof input === 'function' ? input({ pageSize, pageNumber }) : input;
      if (data.pageSize) setPageSize(AllCallsTable.Voicemails, data.pageSize);
      if (data.pageNumber) setPageNumber(AllCallsTable.Voicemails, data.pageNumber);
    },
    [setPageSize, setPageNumber, pageSize, pageNumber]
  );

  const setFilters = useCallback(
    (filters: VoicemailFiltersType) => {
      setVoicemailPageFilters(AllCallsTable.Voicemails, filters);
      // Reset page number to 1 when filters are changed
      setPageConfig({ pageNumber: 1 });
      setSelectedTimerange(getTimePeriod(filters.filterOptions.startTime ?? '', filters.filterOptions.endTime ?? ''));
    },
    [setVoicemailPageFilters, setPageConfig, setSelectedTimerange]
  );

  const filtersInStore = config[AllCallsTable.Voicemails].filters;
  const filters = filtersInStore || defaultFilters;

  useOnScopeChange(() => {
    setFilters({ ...filters, locationIds: [] });
  });

  useEffect(() => {
    if (!filtersInStore) {
      setFilters(filters);
    }
  }, [filters, filtersInStore, setFilters]);

  const searchQuery = useMemo(() => `${JSON.stringify({ ...filters })}-${pageSize}`, [filters, pageSize]);

  const { data: voicemailBoxList, isLoading: isVoicemailBoxListLoading } = useVoicemailBoxList({
    phoneConfig,
    locationIds: selectedLocationIds,
  });

  const mailboxToNameMap = getMailboxToNameMap(voicemailBoxList ?? []);

  const { data: mappedTags = {}, isLoading: isTagDataLoading } = TagsV2.Queries.useListTagsQuery({
    request: { orgId: selectedOrgId, groupIds: selectedLocationIds },
    options: {
      select: (data) => {
        return data?.tags.reduce<Record<string, Tag>>((acc, tag) => {
          acc[tag.id] = tag;
          return acc;
        }, {});
      },
    },
  });

  const infiniteQueryProps = useScopedInfiniteQuery<VoicemailInfiniteQueryData, unknown>({
    queryKey: queryKeys.multiVoicemails(searchQuery),
    queryFn: async ({ pageParam }) => {
      const CANARY = 1;
      const { voicemailMessages: data = [] } = await VoicemailApi.listMultiVoicemailMessages({
        // If no locationIds are selected, we should use all available locations
        locationIds: filters.locationIds.length ? filters.locationIds : selectedLocationIds,
        syncDeviceSipProfileId: phoneConfig?.sipProfileId,
        filter: {
          ...filters.filterOptions,
          startTime: formatStartDateWithTimeZone(filters.filterOptions.startTime),
          endTime: formatEndDateWithTimeZone(filters.filterOptions.endTime),
        },
        // We need to fetch more than the requested page size to determine if there is a next page
        limit: pageSize + CANARY,
        page: {
          lastVoicemailId: pageParam?.lastId,
          lastRecordTimestamp: pageParam?.createdAt,
        },
      });

      const parsedData: VoicemailTableDataRow[] = data.map((voicemail: VoicemailMessage) =>
        createVoicemailObject(voicemail, selectedLocationIds, mappedTags, mailboxToNameMap)
      );

      const lastRowIndex = pageSize + CANARY > data.length ? data.length - 1 : data.length - 1 - CANARY;
      const lastRowData = parsedData[lastRowIndex];

      if (!pageParam?.lastId) {
        // whenever 1st page of voicemail data is fetched, we need to update the unread count as well to be consistent
        queryClient.refetchQueries([
          selectedLocationIds,
          ...PhoneCallsQueries.queryKeys.unreadVoicemailCount(phoneConfig?.sipProfileId ?? ''),
        ]);
      }

      const actualSlice = parsedData.slice(0, pageSize);

      return {
        rows: [...actualSlice],
        meta: lastRowData
          ? {
              lastId: lastRowData.voicemailId,
              createdAt: lastRowData.createdAt,
              hasNext: parsedData.length > pageSize,
            }
          : {
              lastId: undefined,
              createdAt: undefined,
              hasNext: false,
            },
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage?.meta.hasNext ? lastPage.meta : undefined;
    },
    retry: false,
    refetchOnWindowFocus: false,
    // Parsing the data depends on the mailboxMap
    enabled: !!voicemailBoxList && !isTagDataLoading && !isVoicemailBoxListLoading,
  });

  const { voicemailData, setVoicemailData: setVoicemailDetailsData } = useVoicemailDetailsShallowStore(
    'voicemailData',
    'setVoicemailData'
  );
  const { enqueue } = useQueueStore.getState();

  const mutations = useVoicemailMutations({
    selectedLocationIds,
    onDeleteSuccess: ({ voicemailId }) => {
      if (voicemailData?.voicemailId === voicemailId) {
        enqueue('voicemail-details', {
          payload: {
            type: 'close',
          },
        });
      }
    },
    optimisticUpdateProps: {
      updateType: 'many',
      pageSize,
      pageNumber,
      queryKey: queryKeys.multiVoicemails(searchQuery).filter(Boolean),
      fetchNextPage: infiniteQueryProps.fetchNextPage,
      onUpdateMutate: (data) => {
        if (data?.voicemailId === voicemailData?.voicemailId) {
          setVoicemailDetailsData(data);
        }
      },
      onUpdateError: (oldData) => {
        if (oldData?.voicemailId === voicemailData?.voicemailId) {
          setVoicemailDetailsData(oldData);
        }
      },
    },
  });

  const { deleteVoicemail: onDelete, updateReadStatus } = mutations;

  const fullQueryKey = useMemo(
    () => [selectedLocationIds, ...queryKeys.multiVoicemails(searchQuery).filter(Boolean)],
    [selectedLocationIds, searchQuery]
  );

  const getVoicemailData = useCallback(
    () => queryClient.getQueryData<InfiniteData<VoicemailQueryData>>(fullQueryKey),
    [queryClient, fullQueryKey]
  );

  const setVoicemailData = useCallback(
    (data: InfiniteData<VoicemailQueryData>) => queryClient.setQueryData(fullQueryKey, data),
    [queryClient, fullQueryKey]
  );
  const { forwardVoicemailModalData, openForwardVoicemailModal, forwardVoicemailModalProps } =
    useForwardVoicemailModalControl({
      voicemailBoxList: voicemailBoxList ?? [],
      onDelete,
    });

  const { onNewVoicemailMessage } = useAddVoicemailMessage(
    getVoicemailData,
    setVoicemailData,
    selectedLocationIds,
    mappedTags,
    mailboxToNameMap
  );

  const { handleVoicemailTagEvent } = useVoicemailTagEvent();

  // Subscribe to voicemail events via the independent emitter
  useVoicemailEventSubscription('voicemail:update', ({ event }) => {
    /**
     * TODO: These events could originate from the same client that triggered them - I could be on Client A, marking a voicemail as read, and then later receive
     * a websocket event for that very action, in Client A itself.
     *
     * We need a way to identify if the event originated from the same client, and if so, ignore it.
     */
    if (event.type === VoicemailEventType.VOICEMAIL_EVENT_TYPE_NEW) {
      // Show the new voicemail update if it matches a selected mailbox or no filter is applied.
      // Also, ensure the status filter is set to unread or all.
      // Also, ensure that the time filter includes today's date to prevent updates for past periods.
      const isTodayIncluded =
        !filters.filterOptions.endTime || dayjs(filters.filterOptions.endTime).isSame(dayjs(), 'day');
      const hasUpdateForSelectedMailbox =
        !filters.filterOptions.mailboxIds?.length || filters.filterOptions.mailboxIds.includes(event.mailboxId);

      if (
        isTodayIncluded &&
        hasUpdateForSelectedMailbox &&
        (filters.filterOptions.status === MessageStatus.MESSAGE_STATUS_UNREAD ||
          filters.filterOptions.status === MessageStatus.MESSAGE_STATUS_ALL)
      ) {
        // add new voicemail only if the filter is not applied
        onNewVoicemailMessage(event);
      }
    } else {
      const newData = updateVoicemailData(event, getVoicemailData());
      if (newData) {
        setVoicemailData(newData);
      }
    }
  });

  useVoicemailEventSubscription('voicemail:tag', ({ event }) => handleVoicemailTagEvent(event));

  /**
   * There is a small window where the table is rendered even when the container query matches (`isSmallMode` is true).
   * This can cause the table to render in a mobile view when it shouldn't.
   */
  const [isSmallMode] = useTargetElementContainerQuery({
    maxWidth: breakpoints.xsmall.max,
    element: document.querySelector('#main-content') as HTMLElement,
  });

  const searchHandler = (searchText: string) => {
    const sanitizedNumber = sanitizePhoneNumber(searchText);
    const numberSearch = isPhoneNumber(sanitizedNumber) || isPartialPhoneNumber(sanitizedNumber) ? sanitizedNumber : '';
    const nameSearch = !isPhoneNumber(sanitizedNumber) && !isPartialPhoneNumber(sanitizedNumber) ? searchText : '';

    if (!!isPartialPhoneNumber(searchText)) {
      setEmptyState({
        type: 'voicemail',
        header: t('Phone Number Searches Must be Exactly 10 Digits'),
        description: t('Phone number searches must be exactly 10 digits in order to return a match.'),
      });
    } else {
      setEmptyState(defaultEmptyState as EmptyStateConfig);
    }

    setFilters({
      ...filters,
      filterOptions: {
        ...filters.filterOptions,
        callerNumber: numberSearch,
        callerName: nameSearch,
      },
    });
  };

  const getVoicemailActions = useVoicemailActionsGetter({
    searchQuery,
    selectedLocationIds,
    openForwardVoicemailModal,
    updateReadStatus,
    deleteVoicemail: onDelete,
  });

  const panelProps = useMemo(
    () => ({
      updateReadStatus,
      deleteVoicemail: onDelete,
      voicemailsQueryKey: queryKeys.multiVoicemails(searchQuery).filter(Boolean),
      sectionFocus: 'transcript' as const,
    }),
    [searchQuery, updateReadStatus, onDelete]
  );

  return (
    <>
      <Forwardvoicemail modalProps={forwardVoicemailModalProps} forwardData={forwardVoicemailModalData} />
      <div css={styles.wrapper}>
        {!isSmallMode ? (
          <VoicemailTableView
            isLoading={infiniteQueryProps.isLoading || isVoicemailBoxListLoading || isTagDataLoading}
            getVoicemailActions={getVoicemailActions}
            infiniteQueryProps={infiniteQueryProps}
            selectedLocationIds={selectedLocationIds}
            emptyState={emptyState}
            panelProps={panelProps}
            setPageConfig={setPageConfig}
            pageNumber={pageNumber}
            pageSize={pageSize}
            filterConfig={{
              setFilters,
              filters,
              voicemailBoxList: voicemailBoxList || [],
              defaultFilters,
              mappedTags,
              searchHandler,
            }}
          />
        ) : (
          <SmallView
            getVoicemailActions={getVoicemailActions}
            infiniteQueryProps={infiniteQueryProps}
            selectedLocationIds={selectedLocationIds}
            setFilters={setFilters}
            filters={filters}
            voicemailBoxList={voicemailBoxList || []}
            defaultFilters={defaultFilters}
            mappedTags={mappedTags}
            searchHandler={searchHandler}
            emptyState={emptyState}
            panelProps={panelProps}
          />
        )}
      </div>
    </>
  );
};

const styles = {
  wrapper: css`
    flex: 1;
    max-width: 100%;
    height: 100%;
    overflow: hidden;
    position: relative;
  `,
};
